/**
 * Seed data for the profile-migrations mock API.
 *
 * These migrations deliberately cover three meaningful UX states the
 * MigrationDashboard needs to render cleanly:
 *
 *   1. Active MAJOR canary at Day 3 / 25% — the primary screen users land on
 *   2. COMPLETED PATCH auto-apply — collapsed/archived style
 *   3. PENDING MINOR with MANUAL_HOLD opt-outs — awaiting operator nudge
 */
import type {
  MigrationRunRecord,
  ProfileMigrationRecord,
} from './types';

const NOW = Date.now();
const iso = (offsetMs: number) => new Date(NOW + offsetMs).toISOString();
const HOURS = 60 * 60 * 1000;
const DAYS = 24 * HOURS;

export const SEED_MIGRATIONS: ProfileMigrationRecord[] = [
  {
    id: 'mig-groomer-120-200',
    subVerticalKey: 'groomer_salon',
    subVerticalDisplayName: 'Pet Kuaförü (Salon)',
    fromVersion: '1.2.0',
    toVersion: '2.0.0',
    bumpType: 'major',
    migrationClass: 'canary',
    hookModule: 'grooming.v1_to_v2.migration',
    breakingChanges: [
      { field: 'pricingDefaults.formula', change: 'string → AST (ADR-104)' },
      { field: 'terminology.patient', change: 'renamed to terminology.customer' },
      {
        field: 'scheduling.overbookingPolicy',
        change: 'new required value "allowed_with_approval"',
      },
    ],
    requiresDataMigration: true,
    rollbackPossible: true,
    createdAt: iso(-7 * DAYS),
    createdBy: 'usr-admin-01',
    startedAt: iso(-3 * DAYS),
    state: 'IN_PROGRESS',
    canaryPercentage: 25,
    nextGatePercentage: 75,
    paused: false,
    stats: {
      eligible: 1240,
      migrated: 312,
      deferred: 14,
      failed: 3,
      manualHold: 5,
      pending: 906,
    },
  },
  {
    id: 'mig-vet-100-101',
    subVerticalKey: 'vet_clinic',
    subVerticalDisplayName: 'Fiziksel Veteriner Kliniği',
    fromVersion: '1.0.0',
    toVersion: '1.0.1',
    bumpType: 'patch',
    migrationClass: 'auto',
    breakingChanges: [],
    requiresDataMigration: false,
    rollbackPossible: true,
    createdAt: iso(-10 * DAYS),
    createdBy: 'usr-admin-01',
    startedAt: iso(-10 * DAYS),
    state: 'COMPLETED',
    canaryPercentage: 100,
    paused: false,
    stats: {
      eligible: 86,
      migrated: 86,
      deferred: 0,
      failed: 0,
      manualHold: 0,
      pending: 0,
    },
  },
  {
    id: 'mig-groomer-200-210',
    subVerticalKey: 'groomer_salon',
    subVerticalDisplayName: 'Pet Kuaförü (Salon)',
    fromVersion: '2.0.0',
    toVersion: '2.1.0',
    bumpType: 'minor',
    migrationClass: 'hook',
    hookModule: 'grooming.v2_to_v2_1.migration',
    breakingChanges: [],
    requiresDataMigration: true,
    rollbackPossible: true,
    createdAt: iso(-1 * DAYS),
    createdBy: 'usr-admin-01',
    state: 'PENDING',
    canaryPercentage: 0,
    nextGatePercentage: 5,
    paused: false,
    stats: {
      eligible: 312,
      migrated: 0,
      deferred: 0,
      failed: 0,
      manualHold: 8,
      pending: 304,
    },
  },
];

export const SEED_RUNS: Record<string, MigrationRunRecord[]> = {
  'mig-groomer-120-200': [
    {
      id: 'run-001',
      migrationId: 'mig-groomer-120-200',
      tenantId: 'ten-aurora-pet',
      tenantName: 'Aurora Pet Spa',
      state: 'FAILED',
      startedAt: iso(-2 * DAYS),
      completedAt: iso(-2 * DAYS + 5 * 60_000),
      recordsMigrated: 0,
      errorMessage:
        'pricing_formula_parse_error: custom formula "price*1.2+fixed" could not be mapped to AST node',
      correlationId: 'corr-001',
      attempts: 2,
      lastAttemptAt: iso(-1 * DAYS),
    },
    {
      id: 'run-002',
      migrationId: 'mig-groomer-120-200',
      tenantId: 'ten-parkway-wag',
      tenantName: 'Parkway Wag & Shine',
      state: 'FAILED',
      startedAt: iso(-2 * DAYS - 3 * HOURS),
      completedAt: iso(-2 * DAYS - 3 * HOURS + 90_000),
      recordsMigrated: 0,
      errorMessage:
        'terminology_conflict: tenant override still references "patient" key after rename',
      correlationId: 'corr-002',
      attempts: 1,
      lastAttemptAt: iso(-2 * DAYS - 3 * HOURS),
    },
    {
      id: 'run-003',
      migrationId: 'mig-groomer-120-200',
      tenantId: 'ten-mutbucket',
      tenantName: 'MutBucket Grooming Co.',
      state: 'FAILED',
      startedAt: iso(-6 * HOURS),
      completedAt: iso(-6 * HOURS + 40_000),
      recordsMigrated: 12,
      errorMessage:
        'transaction_timeout: migration exceeded 30s per-batch budget (6K rows)',
      correlationId: 'corr-003',
      attempts: 3,
      lastAttemptAt: iso(-2 * HOURS),
    },
    {
      id: 'run-004',
      migrationId: 'mig-groomer-120-200',
      tenantId: 'ten-noble-paws',
      tenantName: 'Noble Paws Boutique',
      state: 'DEFERRED',
      correlationId: 'corr-004',
      attempts: 1,
      lastAttemptAt: iso(-12 * HOURS),
      errorMessage:
        'can_migrate_guard: tenant has 3 in-progress bookings; will retry next gate',
    },
    {
      id: 'run-005',
      migrationId: 'mig-groomer-120-200',
      tenantId: 'ten-enterprise-alpha',
      tenantName: 'Alpha Pet Enterprise',
      state: 'MANUAL_HOLD',
      correlationId: 'corr-005',
      attempts: 0,
      errorMessage: 'Enterprise opt-out — scheduled for Day 6 window.',
    },
  ],
  'mig-vet-100-101': [],
  'mig-groomer-200-210': [],
};
